import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import Mail from '@mui/icons-material/Mail';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import RemoveCircle from '@mui/icons-material/RemoveCircle';
import Sms from '@mui/icons-material/Sms';
import ToggleOffOutlined from '@mui/icons-material/ToggleOffOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  AuthMethod,
  ModelError_Item,
  ModelError_Response,
} from '@treadinc/horizon-api-spec';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { cloneDeep } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import DataGridPager from '~components/DataGrid/DataGridPager';
import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import DeactivateConfirmationDialog from '~components/DeactivateConfirmationDialog/DeactivateConfirmationDialog';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { CompanySelectorFormField } from '~components/FormFields/CompanySelectorFormField';
import { InvitesToSendToggleGroup } from '~components/FormFields/v2/ToggleButtonInput';
import { SimpleMenu } from '~components/Menu/SimpleMenu';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import {
  UserForm,
  UserFormHandler,
  UserFormStateChangeProps,
  UserValidationDTOWithSitesDiff,
} from '~components/UserForm/UserForm';
import UserNotificationsPreferenceForm, {
  UserNotificationsPreferenceDTO,
  UserNotificationsPreferenceHandler,
} from '~components/UserForm/UserNotificationsPreferenceForm';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { SystemRoles } from '~constants/enums';
import { FormStateChangeProps } from '~formsShared/types';
import useCompaniesAdmin from '~hooks/admin/useCompaniesAdmin/useCompaniesAdmin';
import { AdminUser } from '~hooks/admin/useUsersAdmin/models';
import { useUsersAdmin } from '~hooks/admin/useUsersAdmin/useUsersAdmin';
import { CompanyBasic } from '~hooks/useCompany/models';
import useCursorPagination from '~hooks/useCursorPagination/useCursorPagination';
import { useDataGridSearch } from '~hooks/useDataGridSearch/useDataGridSearch';
import { useResourceScopes } from '~hooks/useResourceScopes/useResourceScopes';
import { RoleItem, User } from '~hooks/useUsers/models';
import { useUserRoles } from '~hooks/useUsers/useUserRoles';
import useUsers from '~hooks/useUsers/useUsers';
import { ADMIN_ACTIONS_COLUMN_FIELD } from '~pages/Admin/adminActionsColumnField';
import reportRequestError from '~services/reportRequestError';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';
import { dateFormat } from '~utils/dateTime';
import { formatPhoneForDisplay } from '~utils/utilFunctions';

import { DatadogLink } from '../External/DatadogLink';
import { SentryLink } from '../External/SentryLink';

interface InvitesToSendState {
  email: { isDisabled: boolean; shouldSend: boolean };
  sms: { isDisabled: boolean; shouldSend: boolean };
}

interface DialogState {
  isOpen: boolean;
  availableAuthMethods: AuthMethod[];
  userRolesOptions: RoleItem[];
}

const AdminUsersDataGrid = () => {
  const { userStore } = useStores();

  const {
    control,
    formState: { errors },
    watch,
  } = useForm();

  const [asyncErrors, setAsyncErrors] = useState<ModelError_Item[]>([]);
  const [dialogState, setDialogState] = useState<DialogState>({
    isOpen: false,
    availableAuthMethods: [],
    userRolesOptions: [],
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [userIdToDeactivate, setUserIdToDeactivate] = useState<string>();
  const [userFormIsDirty, setUserFormIsDirty] = useState(false);
  const [
    userNotificationsPreferenceFormIsDirty,
    setUserNotificationsPreferenceFormIsDirty,
  ] = useState(false);

  const formIsDirty = userFormIsDirty || userNotificationsPreferenceFormIsDirty;

  const selectedCompany: Nullable<CompanyBasic> = watch('users-companies-selector');
  const [invitesToSend, setInvitesToSend] = useState<InvitesToSendState>();
  const [isUpdatingUser, setIsUpdatingUser] = useState(false);
  const [isDeactivatingUser, setIsDeactivatingUser] = useState(false);
  const [isFetchingAuthMethods, setIsFetchingAuthMethods] = useState(false);

  const { toasterStore } = useStores();
  const {
    getCompanyUsers,
    impersonateUser,
    updateUser,
    createUser,
    deleteUser,
    revokeAllUserSessions,
    getAllUsers,
  } = useUsersAdmin();
  const { getCompanyById } = useCompaniesAdmin();
  const { getCompanyUserRoles } = useUserRoles();
  const { sendInvitation } = useUsers();
  const { saveUserSitesDiff } = useResourceScopes();

  const { searchValue, setSearchQueryValue } = useDataGridSearch();

  const selectedCompanyId = selectedCompany?.id;

  const {
    goToPage: goToUsersPage,
    isFetching,
    pagination,
    reloadCurrentPage: reloadUsersPage,
    rows,
    startOver: startUsersOver,
  } = useCursorPagination<AdminUser>({
    fetchPage: ({ cursor, limit, link, signal }) =>
      selectedCompanyId
        ? getCompanyUsers({
            companyId: selectedCompanyId,
            cursor,
            limit,
            link,
            searchQuery: searchValue,
            signal,
          })
        : getAllUsers({ cursor, limit, link, searchQuery: searchValue, signal }),
    limit: MAX_API_PAGE_SIZE,
    onFetchError: (error) =>
      reportRequestError(error, t('error_messages.users.failed_to_fetch')),
  });

  useEffect(() => {
    void startUsersOver();
  }, [selectedCompanyId, searchValue, startUsersOver]);

  const openUserDialog = useCallback(
    async (companyId: string) => {
      setIsFetchingAuthMethods(true);

      try {
        const [company, userRolesOptions] = await Promise.all([
          getCompanyById(companyId),
          getCompanyUserRoles(companyId),
        ]);
        if (!userRolesOptions) {
          setSelectedId(null);

          return;
        }

        setDialogState({
          isOpen: true,
          availableAuthMethods: company.authMethods,
          userRolesOptions,
        });
      } catch {
        // Error toast is handled in the hook.
      } finally {
        setIsFetchingAuthMethods(false);
      }
    },
    [getCompanyById, getCompanyUserRoles],
  );

  const onChangeInvite = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name as 'email' | 'sms';
    const checked = event.target.checked;

    setInvitesToSend((state) => {
      const newState = cloneDeep(state);

      if (newState) {
        newState[name] = {
          ...newState[name],
          shouldSend: checked,
        };
      }

      return newState;
    });
  }, []);

  const updateDialogRef = useRef<UserFormHandler>(null);
  const userNotificationsPreferenceRef = useRef<UserNotificationsPreferenceHandler>(null);

  const handleClose = useCallback(() => {
    setDialogState((state) => ({ ...state, isOpen: false }));
    setAsyncErrors([]);
  }, []);

  const editRow = useCallback(
    async (id: string) => {
      setSelectedId(id);

      // when no company is selected we use the company from the selected user
      //
      // NOTE: adminUser is not the Admin User viewing the page, it's actually the user
      // we are editing. it's just named horribly.
      const adminUser = rows.find((user) => user.id === id);
      const companyId = selectedCompany?.id || adminUser?.company?.id;
      if (companyId) {
        if (adminUser?.isActiveMember && adminUser?.isActiveUser) {
          setInvitesToSend(undefined);
        } else {
          setInvitesToSend({
            email: { isDisabled: true, shouldSend: false },
            sms: { isDisabled: true, shouldSend: false },
          });
        }

        await openUserDialog(companyId);
      }
    },
    [selectedCompany, rows, openUserDialog],
  );

  const createNew = useCallback(async () => {
    setSelectedId(null);

    if (selectedCompany) {
      setInvitesToSend({
        email: { isDisabled: true, shouldSend: false },
        sms: { isDisabled: true, shouldSend: false },
      });
      await openUserDialog(selectedCompany.id);
    }
  }, [selectedCompany, openUserDialog]);

  const resendInvite = async (rowId: string, method: 'email' | 'sms') => {
    if (rowId) {
      try {
        await sendInvitation(
          rowId,
          method,
          selectedCompanyId || rows.find((user) => user.id === rowId)?.company?.id,
        );
        toasterStore.push(alert(t('user.invitation_sent'), AlertTypes.success));
      } catch {
        // Error toast is handled in the hook.
      }
    }
  };

  const handleDeactivateUser = useCallback(async () => {
    if (!userIdToDeactivate) {
      return;
    }

    setIsDeactivatingUser(true);
    let didDeactivate = false;
    try {
      didDeactivate = await deleteUser(
        userIdToDeactivate,
        selectedCompanyId ||
          rows.find((user) => user.id === userIdToDeactivate)?.company?.id,
      );
    } finally {
      setIsDeactivatingUser(false);
    }

    if (didDeactivate) {
      toasterStore.push(
        alert(t('administration.users.user_deactivated'), AlertTypes.success),
      );
      setUserIdToDeactivate(undefined);
      void reloadUsersPage();
    }
  }, [
    userIdToDeactivate,
    deleteUser,
    reloadUsersPage,
    rows,
    selectedCompanyId,
    toasterStore,
  ]);

  const handleErrors = (error: AxiosError<ModelError_Response>) => {
    setAsyncErrors(error.response?.data.error.errors || []);
  };

  const onSuccess = async (
    user?: AdminUser,
    formData?: UserValidationDTOWithSitesDiff,
  ) => {
    await saveUserSitesDiff(
      String(user?.id),
      formData?.sitesDiff ?? { siteIdsToAdd: [], resourceIdsToDelete: [] },
      selectedCompanyId || (formData?.company as { id?: string } | null | undefined)?.id,
    );

    handleClose();
    void (selectedId ? reloadUsersPage() : goToUsersPage());
    const name = `${user?.firstName} ${user?.lastName}`;
    toasterStore.push(
      alert(
        selectedId ? t('user.user_updated', { name }) : t('user.user_created', { name }),
        AlertTypes.success,
      ),
    );
  };

  const onSubmitCallback = (
    data: UserValidationDTOWithSitesDiff,
    userNotificationsPreferenceData?: UserNotificationsPreferenceDTO,
  ) => {
    const shouldSendEmailInvite = Boolean(
      !invitesToSend?.email.isDisabled && invitesToSend?.email.shouldSend,
    );
    const shouldSendSmsInvite = Boolean(
      !invitesToSend?.sms.isDisabled && invitesToSend?.sms.shouldSend,
    );

    const userCompanyId =
      selectedCompanyId || (data.company as { id?: string } | null | undefined)?.id;

    if (selectedId) {
      setIsUpdatingUser(true);
      updateUser(
        { id: selectedId, ...data } as unknown as AdminUser,
        userNotificationsPreferenceData,
        userCompanyId,
      )
        .then((adminUser) => {
          return onSuccess(adminUser, data);
        })
        .catch(handleErrors)
        .finally(() => {
          setIsUpdatingUser(false);
        });

      if (shouldSendEmailInvite) {
        void sendInvitation(selectedId, 'email', userCompanyId).catch(() => {
          // Error toast is handled in the hook.
        });
      }

      if (shouldSendSmsInvite) {
        void sendInvitation(selectedId, 'sms', userCompanyId).catch(() => {
          // Error toast is handled in the hook.
        });
      }
    } else {
      setIsUpdatingUser(true);
      createUser(data as unknown as AdminUser, {
        email: shouldSendEmailInvite,
        sms: shouldSendSmsInvite,
      })
        .then((adminUser) => {
          return onSuccess(adminUser, data);
        })
        .catch(handleErrors)
        .finally(() => {
          setIsUpdatingUser(false);
        });
    }
  };

  const onSubmitForm = async () => {
    const userData = await new Promise<UserValidationDTOWithSitesDiff | undefined>(
      (resolve) => {
        updateDialogRef.current?.submit((data) => resolve(data));
      },
    );

    const userNotificationsPreferenceData = await new Promise<
      UserNotificationsPreferenceDTO | undefined
    >((resolve) => {
      if (selectedUser?.id && userNotificationsPreferenceRef.current) {
        userNotificationsPreferenceRef.current.onSubmit?.((data) => resolve(data));
      } else {
        resolve(undefined);
      }
    });

    if (userData) {
      onSubmitCallback(userData, userNotificationsPreferenceData);
    }
  };

  const onFormStateChange = ({
    isDirty,
    hasValidEmail,
    hasValidPhone,
  }: UserFormStateChangeProps) => {
    setUserFormIsDirty(isDirty);

    if (invitesToSend) {
      setInvitesToSend((state) => {
        const newState = cloneDeep(state);

        if (newState) {
          newState.email = {
            ...newState.email,
            isDisabled: !hasValidEmail,
          };

          newState.sms = {
            ...newState.sms,
            isDisabled: !hasValidPhone,
          };
        }

        return newState;
      });
    }
  };

  const onUserNotificationsPreferenceFormStateChange = useCallback(
    ({ isDirty }: FormStateChangeProps) => {
      setUserNotificationsPreferenceFormIsDirty(isDirty);
    },
    [],
  );

  const isGridBusy =
    isFetching || isUpdatingUser || isDeactivatingUser || isFetchingAuthMethods;

  const selectedUser = useMemo(() => {
    return (
      rows.find((user) => user.id === selectedId) ||
      ({
        company: selectedCompany,
      } as AdminUser)
    );
  }, [selectedId, rows]);

  const onChangeQuickFilter = (searchQuery: string) => {
    setSearchQueryValue(searchQuery);
  };

  const permissions = useMemo(() => userStore.getPermissions(), []);

  const columns = useMemo<Array<DataTableColumn<AdminUser>>>(
    () => [
      {
        field: 'lastName',
        headerName: t('form_fields.last_name'),
        renderCell: (user) => (
          <div title={user.lastName} role={'presentation'} data-sentry-mask>
            {user.lastName}
          </div>
        ),
      },
      {
        field: 'firstName',
        headerName: t('form_fields.first_name'),
        renderCell: (user) => (
          <div title={user.firstName} role={'presentation'} data-sentry-mask>
            {user.firstName}
          </div>
        ),
      },
      {
        field: 'company',
        headerName: t('form_fields.company'),
        valueGetter: (user) => user.company?.legalName || '',
      },
      {
        field: 'roles',
        headerName: t('form_fields.role'),
        valueGetter: (user) => (user.userRoles ?? []).map((role) => role.name).join(', '),
      },
      {
        field: 'email',
        headerName: 'Email',
        renderCell: (user) => (
          <div title={user.email ?? undefined} role={'presentation'} data-sentry-mask>
            {user.email}
          </div>
        ),
      },
      {
        field: 'phone',
        headerName: t('form_fields.phone'),
        renderCell: (user) => {
          const formatted = formatPhoneForDisplay(user.phone);
          return (
            <div title={formatted} role={'presentation'} data-sentry-mask>
              {formatted}
            </div>
          );
        },
      },
      {
        field: 'createdAt',
        headerName: t('common.created_on'),
        renderCell: (user) => (
          <Tooltip
            key={`created__${user.id}`}
            title={`${t('common.last_update')}: ${dateFormat(user.updatedAt)}`}
          >
            <span>{dateFormat(user.createdAt)}</span>
          </Tooltip>
        ),
      },
      {
        field: 'id',
        headerName: t('form_fields.id'),
        renderCell: (user) => (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body2">{user.id}</Typography>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '4px' }}>
              <DatadogLink userId={user.id} />
              <SentryLink userId={user.id} />
            </div>
          </Box>
        ),
      },
      {
        field: 'status',
        headerName: t('form_fields.status'),
        valueGetter: (user) =>
          `${user.status.charAt(0).toUpperCase()}${user.status.slice(1)}`,
      },
      {
        field: 'lastLoginAt',
        headerName: t('user.last_login_at'),
        renderCell: (user) => {
          const lastLoginAt = user.lastLoginAt;

          if (lastLoginAt) {
            const startTime = dayjs(lastLoginAt).tz().format('hh:mm A');
            const startDate = dayjs(lastLoginAt).tz().format('DD-MMM-YYYY');

            return (
              <Box
                sx={{
                  display: 'flex' as const,
                  flexDirection: 'column',
                  justifyContent: 'center',
                  width: '100%',
                  lineHeight: 1.25,
                }}
              >
                <Box>{startTime}</Box>
                <Box>{startDate}</Box>
              </Box>
            );
          }

          return '-';
        },
      },
      actionsColumn((user) => {
        const options = [];

        if (!user.isActiveMember && user.email) {
          options.push({
            title: t('user.resend_email_invitation'),
            icon: <Mail />,
            callBack: () => resendInvite(user.id, 'email'),
          });
        }

        if (user.phone && !user.isActiveUser) {
          options.push({
            title: t('user.resend_sms_invitation'),
            icon: <Sms />,
            callBack: () => resendInvite(user.id, 'sms'),
          });
        }

        const targetCompanyId = selectedCompanyId || user.company?.id;

        if (targetCompanyId) {
          options.push({
            title: 'Revoke Sessions',
            icon: <RemoveCircle />,
            callBack: () => {
              revokeAllUserSessions(user.id, targetCompanyId);
            },
          });
        }

        const targetUserIsPlatformAdmin = user.userRoles?.some(
          (role) => role.name === SystemRoles.PLATFORM_ADMIN,
        );

        if (permissions.canDeleteUser && !targetUserIsPlatformAdmin) {
          options.push({
            title: t('actions.deactivate'),
            icon: <ToggleOffOutlined />,
            callBack: () => setUserIdToDeactivate(user.id),
          });
        }

        return (
          <>
            {permissions.canImpersonate && (
              <IconButton
                aria-label={t('actions.impersonate')}
                size="small"
                disabled={!targetCompanyId}
                onClick={() => {
                  if (targetCompanyId) {
                    impersonateUser(user.id, targetCompanyId);
                  }
                }}
              >
                <FingerprintIcon />
              </IconButton>
            )}
            <IconButton
              aria-label={t('actions.edit')}
              size="small"
              onClick={() => editRow(user.id)}
            >
              <Edit />
            </IconButton>
            <SimpleMenu options={options}>
              <MoreHoriz />
            </SimpleMenu>
          </>
        );
      }, ADMIN_ACTIONS_COLUMN_FIELD),
    ],
    [editRow, permissions],
  );

  return (
    <>
      <Box
        sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}
      >
        <Box
          data-test-id={'company-selector'}
          sx={{
            display: 'grid',
            width: '30%',
          }}
        >
          <CompanySelectorFormField
            name="users-companies-selector"
            errors={errors}
            control={control}
            hideLabel={true}
            inputProps={{ placeholder: `${t('form_fields.company_selector_hint')}` }}
            sx={{
              flexGrow: 1,
              flexShrink: 0,
            }}
            clearable
          />
        </Box>
        <Button
          size="medium"
          color={'primary'}
          onClick={createNew}
          startIcon={<Add />}
          disabled={!selectedCompany?.id}
        >
          {selectedCompany?.id ? (
            t('user.create_user')
          ) : (
            <Tooltip title={t('user.create_user')}>
              <span>{t('user.create_user')}</span>
            </Tooltip>
          )}
        </Button>
      </Box>
      <Box sx={{ mt: 3 }} data-test-id={'users-data-grid'}>
        <DataTable
          id="admin-users-grid"
          columns={columns}
          rows={rows}
          loading={isGridBusy}
          onSearchChange={onChangeQuickFilter}
          headerTrailingComponent={
            <DataGridPager
              isFetching={isGridBusy}
              onNavigate={(link) => void goToUsersPage(link)}
              pagination={pagination}
            />
          }
        />
      </Box>
      <Dialog
        isOpen={dialogState.isOpen}
        onClose={handleClose}
        preventCloseOnBackdropClick
      >
        <DialogHeader onClose={handleClose}>
          {selectedId ? t('user.update_user') : t('user.create_user')}
        </DialogHeader>

        <DialogContent>
          <UserForm
            ref={updateDialogRef}
            availableAuthMethods={dialogState.availableAuthMethods}
            defaultUser={selectedUser}
            editable={true}
            onFormStateChange={onFormStateChange}
            source="admin_users"
            errors={asyncErrors}
            userRolesOptions={dialogState.userRolesOptions}
          />

          {selectedUser?.id && (
            <UserNotificationsPreferenceForm
              key={selectedUser?.id}
              ref={userNotificationsPreferenceRef}
              onFormStateChange={onUserNotificationsPreferenceFormStateChange}
              user={selectedUser as unknown as User}
            />
          )}
        </DialogContent>

        <DialogActions>
          {invitesToSend && (
            <Box sx={{ mr: 'auto' }}>
              <InvitesToSendToggleGroup
                invitesToSend={invitesToSend}
                isUpdating={isUpdatingUser}
                onChangeInvite={onChangeInvite}
              />
            </Box>
          )}

          <SmallButton
            color="secondary"
            disabled={isUpdatingUser}
            onClick={handleClose}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            disabled={!formIsDirty || isUpdatingUser}
            loading={isUpdatingUser}
            onClick={onSubmitForm}
          >
            {selectedId ? t('actions.update') : t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>

      <DeactivateConfirmationDialog
        id={userIdToDeactivate}
        message={t('administration.users.deactivate_user_dialog_message')}
        onClose={() => setUserIdToDeactivate(undefined)}
        onConfirm={handleDeactivateUser}
        title={t('administration.users.deactivate_user_dialog_title')}
      />
    </>
  );
};

export default observer(AdminUsersDataGrid);
